<script setup lang="ts">
import { Head, Link, router, useForm } from "@inertiajs/vue3"
import { Landmark, Search, SlidersHorizontal, X } from "lucide-vue-next"
import { Pencil, Plus, Trash2 } from "lucide-vue-next"
import { computed, onUnmounted, ref, watch } from "vue"

import AddressForm, { type AddressFields } from "@/components/AddressForm.vue"
import AppDataTable from "@/components/AppDataTable.vue"
import type { TableColumn } from "@/components/AppDataTable.vue"
import AppDialog from "@/components/AppDialog.vue"
import AppPagination from "@/components/AppPagination.vue"
import FormSection from "@/components/FormSection.vue"
import InputError from "@/components/InputError.vue"
import RelativeTime from "@/components/RelativeTime.vue"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { useAuth } from "@/composables/useAuth"
import { useDialog } from "@/composables/useDialog"
import AppLayout from "@/layouts/AppLayout.vue"
import {
  editLenderPath,
  lenderPath,
  lendersPath,
} from "@/routes"
import { type BreadcrumbItem } from "@/types"

interface Lender {
  id: number
  name: string
  contact: string
  email: string
  phone: string
  portal: string | null
  has_api: boolean
  active: boolean
  created_at: string
}

interface PaginationMeta {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Filters {
  q?: string | null
  active?: string | null
  sort?: string | null
  direction?: string | null
}

const props = defineProps<{
  lenders: Lender[]
  pagination: PaginationMeta
  filters: Filters
}>()

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Lenders", href: lendersPath() },
]

const ALL = "__all__"

const search = ref(props.filters.q ?? "")
const activeFilter = ref(props.filters.active ?? ALL)
const sortCol = ref(props.filters.sort ?? "")
const sortDir = ref<"asc" | "desc">((props.filters.direction as "asc" | "desc") ?? "desc")

const hasActiveFilters = computed(
  () => search.value !== "" || activeFilter.value !== ALL,
)

const filterParams = computed(() => ({
  q: search.value || undefined,
  active: activeFilter.value !== ALL ? activeFilter.value : undefined,
  sort: sortCol.value || undefined,
  direction: sortCol.value ? sortDir.value : undefined,
}))

const tableColumns: TableColumn[] = [
  { key: "name", label: "Name", sortable: true },
  { key: "contact", label: "Contact" },
  { key: "email", label: "Email" },
  { key: "phone", label: "Phone" },
  { key: "portal", label: "Portal" },
  { key: "has_api", label: "Has API" },
  { key: "status", label: "Status" },
  { key: "created_at", label: "Created", sortable: true },
  { key: "actions", label: "Actions", align: "right" },
]

let searchTimer: ReturnType<typeof setTimeout>

function applyFilters() {
  const params = Object.fromEntries(
    Object.entries(filterParams.value).filter(([, v]) => v != null),
  )
  router.get(lendersPath(), params, { preserveState: true, replace: true })
}

onUnmounted(() => clearTimeout(searchTimer))

watch(search, () => {
  clearTimeout(searchTimer)
  searchTimer = setTimeout(applyFilters, 350)
})

watch(activeFilter, applyFilters)

function clearFilters() {
  search.value = ""
  activeFilter.value = ALL
  sortCol.value = ""
  sortDir.value = "desc"
}

function handleSort(key: string, dir: "asc" | "desc") {
  sortCol.value = key
  sortDir.value = dir
  applyFilters()
}

// ── Create dialog ─────────────────────────────────────────────────────────
const { isAdmin } = useAuth()
const { confirm } = useDialog()

const showCreateDialog = ref(false)

const form = useForm({
  name: "",
  contact: "",
  email: "",
  phone: "",
  portal: "",
  has_api: false,
  active: true,
  address_attributes: {
    id: undefined as number | undefined,
    unit_no: "",
    street_no: "",
    street_name: "",
    street_type: "",
    city: "",
    state: "",
    postcode: "",
  },
})

const createAddressFields = computed<AddressFields>({
  get: () => ({
    unit_no: form.address_attributes.unit_no,
    street_no: form.address_attributes.street_no,
    street_name: form.address_attributes.street_name,
    street_type: form.address_attributes.street_type,
    city: form.address_attributes.city,
    state: form.address_attributes.state,
    postcode: form.address_attributes.postcode,
  }),
  set: (val) => Object.assign(form.address_attributes, val),
})

function openCreate() {
  form.reset()
  showCreateDialog.value = true
}

function submitCreate() {
  form.post(lendersPath(), {
    preserveScroll: true,
    onSuccess: () => {
      showCreateDialog.value = false
    },
  })
}

// ── Delete ────────────────────────────────────────────────────────────────
async function confirmDelete(lender: Lender) {
  const ok = await confirm({
    title: 'Delete lender?',
    description: `Are you sure you want to delete ${lender.name}? This action cannot be undone.`,
    confirmLabel: 'Delete',
  })
  if (!ok) return
  router.delete(lenderPath(lender.id), { preserveScroll: true })
}

function err(field: string): string[] | undefined {
  const msg = (form.errors as Record<string, string | undefined>)[field]
  return msg ? [msg] : undefined
}
</script>

<template>
  <Head title="Lenders" />

  <AppLayout :breadcrumbs="breadcrumbs">
    <div class="flex h-full flex-1 flex-col gap-4 overflow-x-auto p-4">
      <!-- Page header -->
      <div class="flex items-center justify-between">
        <div class="flex items-center gap-3">
          <Landmark class="text-muted-foreground size-6" />
          <div>
            <h1 class="text-foreground text-xl font-semibold">Lenders</h1>
            <p class="text-muted-foreground text-sm">Manage your lenders</p>
          </div>
        </div>
        <Button @click="openCreate">
          <Plus class="size-4" />
          New Lender
        </Button>
      </div>

      <!-- Table card -->
      <Card class="border-sidebar-border/70 dark:border-sidebar-border">
        <CardHeader class="pb-3">
          <div class="flex items-center justify-between">
            <div>
              <CardTitle class="text-base">All Lenders</CardTitle>
              <CardDescription>
                {{ pagination.total_count }} lender{{
                  pagination.total_count !== 1 ? "s" : ""
                }}{{ hasActiveFilters ? " found" : " total" }}
              </CardDescription>
            </div>
            <SlidersHorizontal class="text-muted-foreground size-4" />
          </div>
        </CardHeader>

        <!-- Search & filters -->
        <div class="border-border border-b px-4 pb-4">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center">
            <!-- Search -->
            <div class="relative flex-1">
              <Search class="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                v-model="search"
                placeholder="Search by name, contact or email…"
                class="pl-9"
              />
            </div>

            <!-- Status filter -->
            <Select v-model="activeFilter">
              <SelectTrigger class="w-full sm:w-36">
                <SelectValue placeholder="Any status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem :value="ALL">Any status</SelectItem>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
              </SelectContent>
            </Select>

            <!-- Clear filters -->
            <Button
              v-if="hasActiveFilters"
              variant="ghost"
              size="sm"
              class="text-muted-foreground hover:text-foreground shrink-0"
              @click="clearFilters"
            >
              <X class="size-4" />
              Clear
            </Button>
          </div>
        </div>

        <CardContent class="p-0">
          <!-- Empty state -->
          <div
            v-if="lenders.length === 0"
            class="flex flex-col items-center justify-center gap-3 py-16 text-center"
          >
            <Landmark class="text-muted-foreground/40 size-12" />
            <p class="text-muted-foreground text-sm">
              {{ hasActiveFilters ? "No lenders match your filters" : "No lenders yet" }}
            </p>
            <Button v-if="hasActiveFilters" variant="outline" size="sm" @click="clearFilters">
              <X class="size-4" />
              Clear filters
            </Button>
            <Button v-else variant="outline" size="sm" @click="openCreate">
              <Plus class="size-4" />
              Add your first lender
            </Button>
          </div>

          <!-- Table -->
          <AppDataTable
            v-else
            :columns="tableColumns"
            :sort="sortCol || undefined"
            :direction="sortDir"
            @sort="handleSort"
          >
            <tr
              v-for="lender in lenders"
              :key="lender.id"
              class="border-border hover:bg-muted/50 border-b transition-colors last:border-0"
            >
              <td class="px-6 py-4 font-medium">
                <Link
                  :href="editLenderPath(lender.id)"
                  class="text-foreground hover:text-primary hover:underline"
                >{{ lender.name }}</Link>
              </td>
              <td class="text-muted-foreground px-6 py-4">{{ lender.contact }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ lender.email }}</td>
              <td class="text-muted-foreground px-6 py-4">{{ lender.phone }}</td>
              <td class="text-muted-foreground px-6 py-4">
                <a
                  v-if="lender.portal"
                  :href="lender.portal"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-primary hover:underline"
                >Portal ↗</a>
                <span v-else class="text-muted-foreground/50">—</span>
              </td>
              <td class="px-6 py-4">
                <Badge :variant="lender.has_api ? 'default' : 'secondary'">
                  {{ lender.has_api ? "Yes" : "No" }}
                </Badge>
              </td>
              <td class="px-6 py-4">
                <Badge :variant="lender.active ? 'default' : 'secondary'">
                  {{ lender.active ? "Active" : "Inactive" }}
                </Badge>
              </td>
              <td class="text-muted-foreground px-6 py-4">
                <RelativeTime :date="lender.created_at" />
              </td>
              <td class="px-6 py-4">
                <div class="flex items-center justify-end gap-2">
                  <Button variant="ghost" size="icon" class="size-8" as-child>
                    <Link :href="editLenderPath(lender.id)">
                      <Pencil class="size-4" />
                      <span class="sr-only">Edit</span>
                    </Link>
                  </Button>
                  <Button
                    v-if="isAdmin"
                    variant="ghost"
                    size="icon"
                    class="text-destructive hover:text-destructive size-8"
                    @click="confirmDelete(lender)"
                  >
                    <Trash2 class="size-4" />
                    <span class="sr-only">Delete</span>
                  </Button>
                </div>
              </td>
            </tr>
          </AppDataTable>

          <!-- Pagination -->
          <AppPagination
            v-if="pagination.total_pages > 1"
            :current-page="pagination.current_page"
            :total-pages="pagination.total_pages"
            :total-count="pagination.total_count"
            :per-page="pagination.per_page"
            :base-url="lendersPath()"
            :extra-params="filterParams"
          />
        </CardContent>
      </Card>
    </div>

    <!-- ── Create Dialog ─────────────────────────────────────────────────── -->
    <AppDialog
      v-model:open="showCreateDialog"
      title="New Lender"
      description="Fill in the details to create a new lender."
      scrollable
      class="sm:max-w-lg"
    >
      <div class="space-y-4">
        <FormSection title="Contact Information">
          <div class="grid gap-2">
            <Label for="c-name">Name</Label>
            <Input id="c-name" v-model="form.name" placeholder="ANZ Bank" />
            <InputError :messages="err('name')" />
          </div>

          <div class="grid grid-cols-2 gap-3">
            <div class="grid gap-2">
              <Label for="c-contact">Contact Person</Label>
              <Input id="c-contact" v-model="form.contact" placeholder="John Smith" />
              <InputError :messages="err('contact')" />
            </div>
            <div class="grid gap-2">
              <Label for="c-phone">Phone</Label>
              <Input id="c-phone" v-model="form.phone" type="tel" placeholder="+61 400 000 000" />
              <InputError :messages="err('phone')" />
            </div>
          </div>

          <div class="grid gap-2">
            <Label for="c-email">Email</Label>
            <Input id="c-email" v-model="form.email" type="email" placeholder="contact@lender.com" />
            <InputError :messages="err('email')" />
          </div>

          <div class="grid gap-2">
            <Label for="c-portal">Portal URL <span class="text-muted-foreground font-normal">(optional)</span></Label>
            <Input id="c-portal" v-model="form.portal" type="url" placeholder="https://portal.lender.com" />
            <InputError :messages="err('portal')" />
          </div>
        </FormSection>

        <div class="border-border flex items-center justify-between rounded-lg border p-4">
          <div>
            <p class="text-sm font-medium">Has API</p>
            <p class="text-muted-foreground text-xs">Enables API submission of applications to this lender.</p>
          </div>
          <Switch id="c-has-api" v-model="form.has_api" />
        </div>

        <div class="border-border flex items-center justify-between rounded-lg border p-4">
          <div>
            <p class="text-sm font-medium">Active</p>
            <p class="text-muted-foreground text-xs">Allow this lender to receive and process loan applications.</p>
          </div>
          <Switch id="c-active" v-model="form.active" />
        </div>

        <FormSection title="Address" :optional="true">
          <AddressForm v-model="createAddressFields" :errors="form.errors" error-prefix="address." />
        </FormSection>
      </div>
      <template #footer>
        <Button variant="outline" @click="showCreateDialog = false">Cancel</Button>
        <Button :disabled="form.processing" @click="submitCreate">Create Lender</Button>
      </template>
    </AppDialog>

  </AppLayout>
</template>
